import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { PreloadedQuery, usePreloadedQuery } from "react-relay"
import { GenderFemale, GenderIntersex, GenderMale, Question } from "@phosphor-icons/react"
import { TFunction } from "i18next"
import { HighlightCellText } from "@/components/highlight-cell-text"
import { ScrollWrapper } from "@/components/scroll-wrapper"
import { useDetectSticky } from "@/hooks/useDetectSticky"
import { useUserStore } from "@/lib/fetch-users"
import { convertLanguageToISOCode } from "@/lib/language-to-iso-code"
import { useAgentSkillsQuery } from "@/modules/admin/agent-skills/services/__generated__/useAgentSkillsQuery.graphql"
import { agentSkillsQuery } from "@/modules/admin/agent-skills/services/useAgentSkillsQuery"
import { Gender, LanguageValues, UserRole } from "@/types/__generated__/union-types"

type AgentSkill = {
  id: string
  userName: string
  role: UserRole
  performancePerSlot: number
  gender: Gender
  languages: string
  skills: string
  licenses: string
  teams: string
}

type AgentSkillsTableProps = {
  queryRef: PreloadedQuery<useAgentSkillsQuery>
}

type ColumnProps<AgentSkill> = { header: string; cell: (props: { data: AgentSkill; t: TFunction }) => React.ReactNode }

const columns: ColumnProps<AgentSkill>[] = [
  {
    header: "Username",
    cell: ({ data }) => {
      const value = data.userName
      return <HighlightCellText text={value} title={value} />
    },
  },
  {
    header: "Role",
    cell: ({ data, t }) => {
      const value = t("common.roleNames." + data.role)
      return <HighlightCellText text={value} title={value} />
    },
  },
  {
    header: "Performance per slot",
    cell: ({ data }) => {
      const value = data.performancePerSlot
      return (
        <div
          title={value.toString()}
          className={value < 2 ? "bg-critical-surface text-on-critical-surface truncate" : "truncate"}
          style={{ width: "40px", textAlign: "center" }}>
          {value.toString()}
        </div>
      )
    },
  },
  {
    header: "Gender",
    cell: ({ data }) => {
      const Icon = getGenderIcon(data.gender)
      return <Icon />
    },
  },
  {
    header: "Languages",
    cell: ({ data }) => {
      const value = data.languages
      return <HighlightCellText text={value} title={value} />
    },
  },
  {
    header: "Skills",
    cell: ({ data }) => {
      const value = data.skills
      return <HighlightCellText text={value} title={value} />
    },
  },
  {
    header: "Licenses",
    cell: ({ data }) => {
      const { licenses, role } = data
      // Nursing professional agents who have only one DE license should be highlighted
      const shouldBeHighlighted = role === "NURSING_PROFESSIONAL" && (licenses === "DE" || licenses === "DE!")

      return (
        <div
          title={licenses.toString()}
          className={
            shouldBeHighlighted ? "bg-critical-surface text-on-critical-surface w-fit truncate px-3" : "truncate"
          }>
          {licenses.toString()}
        </div>
      )
    },
  },
  {
    header: "Teams",
    cell: ({ data }) => {
      const value = data.teams
      return <HighlightCellText text={value} title={value} />
    },
  },
]

export const getGenderIcon = (gender: Gender) => {
  switch (gender) {
    case "MALE":
      return GenderMale
    case "FEMALE":
      return GenderFemale
    case "OTHER":
      return GenderIntersex
    case "UNKNOWN":
      return Question
  }
}

export const AgentSkillsTable: React.FC<AgentSkillsTableProps> = ({ queryRef }) => {
  const { t } = useTranslation()
  const allUsers = useUserStore((store) => store.users)

  const onIntersectionTriggered = (isSticky: boolean) => {
    if (!ref.current) return

    const element = ref.current.querySelector("[data-testid='table-header-container']")
    if (element) {
      element.classList.toggle("table-header-shadow", isSticky)
    }
  }
  const ref = useDetectSticky({ onIntersectionTriggered })

  const { doctors } = usePreloadedQuery<useAgentSkillsQuery>(agentSkillsQuery, queryRef)

  const results: AgentSkill[] = useMemo(() => {
    const agentSkills: AgentSkill[] =
      doctors?.edges
        ?.map((item): AgentSkill => {
          return {
            userName: item.node.userName,
            id: item.node.id,
            role: allUsers?.get(item.node.userName)?.role ?? "NONE",
            performancePerSlot: item.node.performancePerSlot,
            gender: item.node.gender,
            languages: [...item.node.languages]
              .sort((a, b) => {
                const ai = a.skill ? LanguageValues.indexOf(a.skill) : LanguageValues.length
                const bi = b.skill ? LanguageValues.indexOf(b.skill) : LanguageValues.length
                return ai - bi
              })
              .map((language) => {
                if (!language.skill) {
                  return ""
                }

                let result = convertLanguageToISOCode(language.skill)

                if (language.fullySpottable) {
                  result += "*"
                }
                if (language.skill === item.node.preferredLanguage) {
                  result += "+"
                }

                return result
              })
              .filter(Boolean)
              .join(", "),
            skills: item.node.specialties
              .map((specialty) => {
                if (!specialty.skill) {
                  return ""
                }

                let result = specialty.skill

                if (specialty.fullySpottable) {
                  result += "*"
                }

                return result
              })
              .sort()
              .join(", "),
            licenses: item.node.licensesInCountries
              .map((license) => {
                let result = license.country.toString()

                if (license.isFavoredInCountry) {
                  result += "!"
                }

                return result
              })
              .sort()
              .join(", "),
            teams: item.node.teams
              .map((team) => {
                if (!team.skill) {
                  return ""
                }

                let result = team.skill

                if (team.fullySpottable) {
                  result += "*"
                }

                return result
              })
              .sort()
              .join(", "),
          }
        })
        .sort((a, b) => a.userName.localeCompare(b.userName)) || []

    return agentSkills
  }, [doctors?.edges, allUsers])

  const AgentSkillRow = (item: AgentSkill) => {
    const rowKey = item.id
    return (
      <div
        key={rowKey}
        data-testid="table-row"
        className="border-border text-on-surface hover:bg-surface-hovered relative col-span-full grid h-10 cursor-pointer items-center justify-center gap-4 px-4 py-2 text-sm"
        role="button"
        style={{
          gridTemplateColumns:
            "minmax(0,0.5fr) minmax(0,0.4fr) minmax(0,0.2fr) minmax(0,0.2fr) minmax(0,0.2fr) minmax(0,0.6fr) minmax(0,0.2fr) minmax(0,0.3fr)",
        }}>
        {columns.map((column) => (
          <span key={`${rowKey}-${column.header}`} className="truncate" data-testid="table-cell">
            {column.cell({ data: item, t })}
          </span>
        ))}
      </div>
    )
  }

  return (
    <ScrollWrapper className="pb-8">
      <div className="px-8">
        <div className="relative mx-auto grid w-full overflow-x-clip">
          <div className="bg-background-subdued sticky -top-px z-10 pt-px" ref={ref}>
            {results.length > 0 ? (
              <div
                data-testid="table-header-container"
                className="border-border text-on-surface-subdued bg-surface grid gap-4 rounded-t-xl border px-4 py-2 text-xs"
                style={{
                  gridTemplateColumns:
                    "minmax(0,0.5fr) minmax(0,0.4fr) minmax(0,0.2fr) minmax(0,0.2fr) minmax(0,0.2fr) minmax(0,0.6fr) minmax(0,0.2fr) minmax(0,0.3fr)",
                }}>
                {columns.map((column) => (
                  <span key={column.header} className="truncate" title={t(column.header)}>
                    {t(`${column.header}`)}
                  </span>
                ))}
              </div>
            ) : (
              <div className="animate-fade-in-bottom grid h-full content-center items-center justify-center gap-4 p-3 text-center">
                <div className="grid gap-1">
                  <h2 className="text-on-surface text-xl font-bold">{t("common.dataNotFound")}</h2>
                </div>
              </div>
            )}
          </div>
          <div
            data-testid="table-row-container"
            className="border-border bg-surface col-span-full grid divide-y overflow-hidden rounded-b-xl border-x border-b text-sm">
            {results.map((item) => AgentSkillRow(item))}
          </div>
        </div>
      </div>
    </ScrollWrapper>
  )
}
